import React, { useState, useEffect } from "react";
import { Table, Button, Input, message, Tabs, Tag, Spin } from "antd";
import {
  getDocs,
  orderBy,
  query,
  collection,
  updateDoc,
  addDoc,
  where,
  doc,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { debounce } from "lodash";
import "./registration.css";
import BadgePreviewModal from "./BadgePreviewModal";
import CustomCountrySelect from "../CustomCountrySelect";
import { Select } from "antd";
import "./badge.css";
import { generateBadgesPdf } from "../../util/generateBadgesPdf";
import XLSX from "xlsx-js-style";

const { Option } = Select;
const { TabPane } = Tabs;

export type Participants = {
  docId?: string;
  name: string;
  title?: string;
  fname: string;
  lname: string;
  email: string;
  organization: string;
  position: string;
  mobileNumber: string;
  ticketType: string;
  nationality?: string;
  companionName?: string;
  companionRelationship?: string;
  country?: string;
  status?: string;
  notes?: string;
  referralSource?: string;
  referralOther?: string;
  badgeCategory?: string;
  badgeCollected?: string;
};

type Registration = {
  key: string;
  totalAmount: string;
  totalDelegateTickets: string;
  totalUaefmaTickets: string;
  coupon: string;
  timestamp: string;
  contactPersons: Participants[];
  invoiceNumber?: string;
  ticketId?: string;
};

interface ParticipantsWithOrder extends Participants {
  registrationKey: string;
  timestamp: string;
  invoiceNumber?: string;
}

interface PrintHistory {
  name: string;
  badgeType?: string;
  status: string;
  printedAt?: string;
  organization?: string;
  country?: string;
  badgeCategory?: string;
}

const getBadgeColor = (val: string): string => {
  switch (val?.toUpperCase()) {
    case "DELEGATE":
    case "EXHIBITOR":
      return "blue";
    case "COMPANION":
      return "green";
    case "SPEAKER":
    case "MODERATOR":
    case "VIP":
    case "ICA EXECUTIVE":
    case "SPONSOR":
    case "ICA HONORARY":
    case "GUEST":
      return "orange";
    case "ORGANIZER":
    case "MEDIA":
    case "PRESS":
    case "SECURITY":
    case "HOSTESS":
    case "SUPPORT CREW":
      return "red";
    default:
      return "gray";
  }
};

const ManageRegistrationsV3: React.FC = () => {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [filteredData, setFilteredData] = useState<ParticipantsWithOrder[]>([]);
  const [companions, setCompanions] = useState<ParticipantsWithOrder[]>([]);
  const [filteredCompanions, setFilteredCompanions] = useState<
    ParticipantsWithOrder[]
  >([]);
  const [printHistory, setPrintHistory] = useState<PrintHistory[]>([]);
  const [filteredHistory, setFilteredHistory] = useState<PrintHistory[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedCompanionKeys, setSelectedCompanionKeys] = useState<
    React.Key[]
  >([]);
  const [selectedParticipants, setSelectedParticipants] = useState<any[]>([]);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  const [manualParticipant, setManualParticipant] = useState({
    title: "",
    fname: "",
    lname: "",
    organization: "",
    country: "",
    badgeCategory: "",
  });

  const [manualErrors, setManualErrors] = useState<any>({});

  const getFullName = (c: Partial<Participants>) =>
    [c.fname, c.lname].filter(Boolean).join(" ") || c.name || "";

  useEffect(() => {
    fetchRegistrations();
    fetchPrintHistory();
  }, []);

  const fetchRegistrations = async () => {
    try {
      const ordersRef = collection(db, "orders");
      const q = query(ordersRef, orderBy("timestamp", "desc"));
      const orderSnapshot = await getDocs(q);

      const ordersWithContacts: Registration[] = await Promise.all(
        orderSnapshot.docs.map(async (orderDoc) => {
          const orderData = orderDoc.data();
          const orderId = orderDoc.id;
          const timestamp = orderData.timestamp?.toDate
            ? orderData.timestamp.toDate().toLocaleString()
            : "N/A";

          const contactsRef = collection(db, `orders/${orderId}/contacts`);
          const contactsSnapshot = await getDocs(contactsRef);

          const contactPersons: Participants[] = contactsSnapshot.empty
            ? []
            : contactsSnapshot.docs.map((doc) => {
                const d = doc.data();
                const companion = d.companion || {};
                return {
                  docId: doc.id,
                  name: getFullName(d),
                  fname: d.fname || "",
                  lname: d.lname || "",
                  email: d.email || "",
                  organization: d.organization || "",
                  position: d.position || "",
                  mobileNumber: d.mobileNumber || "",
                  ticketType:
                    d.ticketId === "delegate"
                      ? "Delegate"
                      : d.ticketId === "uaefma"
                      ? "UAEFMA Member"
                      : d.ticketId === "exhibitor"
                      ? "Exhibitor"
                      : "Unknown",
                  badgeCategory: d.badgeCategory || "",
                  badgeCollected: d.badgeCollected || "",
                  country: d.country || "",
                  nationality: d.nationality || "",
                  title: d.title || "",
                  companionName: companion.fullName || "",
                  companionRelationship: companion.relationship || "",
                };
              });

          return {
            key: orderId,
            totalAmount: orderData.totalAmount || "0.00",
            totalDelegateTickets: orderData.totalDelegateTickets || "0",
            totalUaefmaTickets: orderData.totalUaefmaTickets || "0",
            coupon: orderData.coupon || "None",
            timestamp,
            contactPersons,
            invoiceNumber: orderData.invoiceNumber,
            ticketId: orderData.ticketId || "N/A",
          };
        })
      );

      setRegistrations(ordersWithContacts);

      const delegates: ParticipantsWithOrder[] = [];
      const companionList: ParticipantsWithOrder[] = [];

      ordersWithContacts.forEach((r) => {
        r.contactPersons.forEach((p) => {
          const base = {
            ...p,
            timestamp: r.timestamp,
            invoiceNumber: r.invoiceNumber,
            registrationKey: r.key,
          };
          delegates.push(base);

          if (p.companionName && p.companionName.trim() !== "") {
            companionList.push({
              ...base,
              fname: p.companionName,
              lname: "",
              email: "",
              title: "",
              ticketType: "Companion",
              country: p.country || "", // ✅ inherit delegate’s country
              organization: p.organization || "", // (optional) inherit organization
            });
          }
        });
      });

      setFilteredData(delegates);
      setCompanions(companionList);
      setFilteredCompanions(companionList);
    } catch (e) {
      console.error(e);
      message.error("Failed to load registrations.");
    }
  };

  const fetchPrintHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const ref = collection(db, "badge_print_history");
      const snap = await getDocs(ref);
      const data = snap.docs.map((d) => d.data() as PrintHistory);
      const sorted = data.sort((a, b) =>
        a.printedAt! < b.printedAt! ? 1 : -1
      );
      setPrintHistory(sorted);
      setFilteredHistory(sorted);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // === SEARCH HANDLERS ===
  const handleSearchDelegates = debounce((text: string) => {
    if (!text.trim()) {
      setFilteredData(
        registrations.flatMap((r) =>
          r.contactPersons.map((p) => ({
            ...p,
            timestamp: r.timestamp,
            invoiceNumber: r.invoiceNumber,
            registrationKey: r.key,
          }))
        )
      );
      return;
    }

    const lower = text.toLowerCase();
    const filtered = registrations.flatMap((r) =>
      r.contactPersons
        .filter((p) =>
          [
            p.fname,
            p.lname,
            p.email,
            p.organization,
            p.country,
            p.ticketType,
          ].some((val) => val?.toLowerCase().includes(lower))
        )
        .map((p) => ({
          ...p,
          timestamp: r.timestamp,
          invoiceNumber: r.invoiceNumber,
          registrationKey: r.key,
        }))
    );

    setFilteredData(filtered);
  }, 300);

  const handleSearchCompanions = debounce((text: string) => {
    if (!text.trim()) {
      setFilteredCompanions(companions);
      return;
    }

    const lower = text.toLowerCase();
    const filtered = companions.filter(
      (c) =>
        c.fname.toLowerCase().includes(lower) ||
        c.companionRelationship?.toLowerCase().includes(lower) ||
        c.name?.toLowerCase().includes(lower)
    );
    setFilteredCompanions(filtered);
  }, 300);

  const handleSearchHistory = debounce((text: string) => {
    if (!text.trim()) {
      setFilteredHistory(printHistory);
      return;
    }

    const lower = text.toLowerCase();
    const filtered = printHistory.filter((h) =>
      h.name.toLowerCase().includes(lower)
    );
    setFilteredHistory(filtered);
  }, 300);

  // === TABLE DEFINITIONS ===
  const columns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_: any, __: any, i: number) =>
        (currentPage - 1) * pageSize + i + 1,
    },
    {
      title: "Full Name",
      key: "name",
      width: 220,
      render: (_: any, r: any) => getFullName(r),
    },
    { title: "Email", dataIndex: "email", width: 200 },
    { title: "Organization", dataIndex: "organization", width: 200 },
    { title: "Country", dataIndex: "country", width: 160 },
    {
      title: "Badge Type",
      dataIndex: "badgeCategory",
      width: 160,
      render: (val: string) => <Tag color={getBadgeColor(val)}>{val}</Tag>,
    },
  ];

  const companionColumns = [
    {
      title: "#",
      key: "index",
      render: (_: any, __: any, i: number) => i + 1,
      width: 50,
    },
    { title: "Companion Name", dataIndex: "fname", width: 240 },
    { title: "Relationship", dataIndex: "companionRelationship", width: 180 },
    { title: "Country", dataIndex: "country", width: 180 },
    { title: "Linked Delegate", dataIndex: "name", width: 240 },
  ];

  const historyColumns = [
    { title: "Name", dataIndex: "name", width: 240 },
    {
      title: "Type",
      dataIndex: "type",
      width: 140,
      render: (val: string) => <Tag color={getBadgeColor(val)}>{val}</Tag>,
    },
    { title: "Status", dataIndex: "status", width: 160 },
    { title: "Printed By", dataIndex: "printedBy", width: 160 },
    {
      title: "Printed At",
      dataIndex: "printedAt",
      width: 200,
      render: (val: string) =>
        val ? new Date(val).toLocaleString("en-US") : "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_: any, record: any) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            size="small"
            onClick={() => handlePreviewHistory(record)}
            icon={<i className="fa fa-eye" />}
          >
            Preview
          </Button>
        </div>
      ),
    },
  ];

  const delegateSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  const companionSelection = {
    selectedRowKeys: selectedCompanionKeys,
    onChange: (keys: React.Key[]) => setSelectedCompanionKeys(keys),
  };

  const handlePreviewHistory = async (record: PrintHistory) => {
    try {
      setLoadingPdf(true);

      const participant = {
        fname: record.name.split(" ")[0] || record.name,
        lname: record.name.split(" ").slice(1).join(" "),
        organization: record.organization || "",
        country: record.country || "",
        badgeCategory: record.badgeType || "",
      };

      const previewBlob = await generateBadgesPdf([participant], true);
      const previewUrl = URL.createObjectURL(previewBlob);
      setSelectedParticipants([participant]);
      setPdfPreviewUrl(previewUrl);
      message.success(`Previewing ${record.name}`);
    } catch (err) {
      console.error(err);
      message.error("Failed to preview badge.");
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleReprintHistory = async (record: PrintHistory) => {
    try {
      const participant = {
        fname: record.name.split(" ")[0] || record.name,
        lname: record.name.split(" ").slice(1).join(" "),
        organization: record.organization || "",
        country: record.country || "",
        badgeCategory: record.badgeCategory || "",
      };

      setSelectedParticipants([participant]);
      message.info(`Preparing reprint for ${record.name}...`);
      await handlePrintBadges();
    } catch (err) {
      console.error(err);
      message.error("Failed to reprint badge.");
    }
  };

  const handleManualBadgePrint = () => {
    const { title, fname, lname, organization, country, badgeCategory } =
      manualParticipant;

    if (!fname || !lname || !country || !badgeCategory) {
      message.warning("Please fill in all required fields.");
      return;
    }

    const participantData = [
      {
        title,
        fname,
        lname,
        organization,
        country,
        badgeCategory,
      },
    ];

    setSelectedParticipants(participantData);
    setIsPreviewVisible(true);
  };

  const [loadingPdf, setLoadingPdf] = useState(false);

  const [printing, setPrinting] = useState(false);

  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfPrintUrl, setPdfPrintUrl] = useState<string | null>(null);

  // === preview generation when selection changes ===
  useEffect(() => {
    const generatePreviews = async () => {
      if (!selectedParticipants.length) {
        setPdfPreviewUrl(null);
        setPdfPrintUrl(null);
        return;
      }

      try {
        setLoadingPdf(true);

        // 🎨 1) Full design for preview
        const previewBlob = await generateBadgesPdf(selectedParticipants, true);
        const previewUrl = URL.createObjectURL(previewBlob);
        setPdfPreviewUrl(previewUrl);

        // 🧾 2) White-only version for printing
        const printBlob = await generateBadgesPdf(selectedParticipants, false); // true for blank
        const printUrl = URL.createObjectURL(printBlob);
        setPdfPrintUrl(printUrl);
      } catch (err) {
        console.error(err);
        message.error("Failed to generate badge preview or print file.");
      } finally {
        setLoadingPdf(false);
      }
    };
    generatePreviews();

    // cleanup on unmount
    return () => {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      if (pdfPrintUrl) URL.revokeObjectURL(pdfPrintUrl);
    };
  }, [selectedParticipants]);

  // === when “Print Selected” is clicked ===
  const handleOpenPreview = (type: "delegate" | "companion") => {
    const keys = type === "delegate" ? selectedRowKeys : selectedCompanionKeys;
    const data =
      type === "delegate" ? filteredData : filteredCompanions || companions;
    if (!keys.length) {
      message.info("Please select at least one record.");
      return;
    }
    const selected = data.filter((p) => {
      const key =
        type === "delegate"
          ? `${p.registrationKey}-${p.fname}-${p.lname}`
          : `${p.registrationKey}-${p.fname}`;
      return keys.includes(key);
    });

    setSelectedParticipants(selected);
  };

  const handleClearSelection = () => {
    setSelectedRowKeys([]);
    setSelectedCompanionKeys([]);
    setSelectedParticipants([]);
    setPdfPreviewUrl(null);
    setPdfPrintUrl(null);
    message.info("Selection cleared.");
  };

  // === PRINT HANDLER ===
  const handlePrintBadges = async () => {
    if (!pdfPrintUrl || !selectedParticipants.length) {
      message.warning("No badges to print.");
      return;
    }

    try {
      setPrinting(true);
      const printedBy = localStorage.getItem("userEmail") || "admin";
      const now = new Date().toISOString();
      const ref = collection(db, "badge_print_history");

      for (const p of selectedParticipants) {
        const nameKey = `${p.title || ""} ${p.fname} ${p.lname}`.trim();
        // Ensure a clean fallback hierarchy
        let badgeCategory = "";

        if (p.badgeCategory && p.badgeCategory.trim() !== "") {
          badgeCategory = p.badgeCategory.trim().toUpperCase();
        } else if (p.ticketType && p.ticketType.trim() !== "") {
          badgeCategory = p.ticketType.trim().toUpperCase();
        } else {
          // try inferring from companion or delegate type
          badgeCategory = p.companionRelationship ? "COMPANION" : "DELEGATE";
        }

        const q = query(ref, where("name", "==", nameKey));
        const snap = await getDocs(q);

        if (!snap.empty) {
          const docRef = doc(db, "badge_print_history", snap.docs[0].id);
          const data = snap.docs[0].data();
          let newStatus = "Reprinted (1)";
          if (data.status?.startsWith("Reprinted")) {
            const match = data.status.match(/\((\d+)\)/);
            const count = match ? parseInt(match[1]) + 1 : 2;
            newStatus = `Reprinted (${count})`;
          }
          await updateDoc(docRef, {
            status: newStatus,
            printedAt: now,
            printedBy,
            type: badgeCategory,
          });
        } else {
          await addDoc(ref, {
            name: nameKey,
            organization: p.organization || "",
            country: p.country || "",
            status: "Printed",
            printedAt: now,
            printedBy,
            type: badgeCategory,
          });
        }
      }

      // ✅ Print via hidden iframe
      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.src = pdfPrintUrl || "";

      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      };

      message.success("Badges printed successfully.");
      fetchPrintHistory();
    } catch (err) {
      console.error(err);
      message.error("Failed to log badge print.");
    } finally {
      setPrinting(false);
    }
  };

  const handleExportExcelByCountry = () => {
    if (!filteredData.length) {
      message.warning("No delegates to export.");
      return;
    }

    const delegates = filteredData.filter(
      (d) => d.badgeCategory?.toLowerCase() === "delegate"
    );
    if (!delegates.length) {
      message.warning("No delegates to export.");
      return;
    }

    const sanitizeSheetName = (name: string) =>
      name
        ?.replace(/[\\\/\?\*\[\]:]/g, "")
        ?.replace(/\s+/g, "_")
        ?.substring(0, 28) || "Unknown";

    const countries = Array.from(
      new Set(delegates.map((d) => d.country || "Unknown"))
    );

    const wb = XLSX.utils.book_new();

    countries.forEach((country) => {
      const safeName = sanitizeSheetName(country);
      const countryDelegates = delegates.filter((d) => d.country === country);

      // Group by organization
      const orgMap: Record<string, ParticipantsWithOrder[]> = {};
      countryDelegates.forEach((p) => {
        const org = p.organization?.trim() || "Unspecified Institution";
        if (!orgMap[org]) orgMap[org] = [];
        orgMap[org].push(p);
      });

      const sortedOrgs = Object.keys(orgMap)
        .sort((a, b) => a.localeCompare(b))
        .map((org) => [org, orgMap[org]] as [string, ParticipantsWithOrder[]]);

      // Build data manually as array of arrays (AOA)
      const aoa: any[][] = [];
      const mergeRanges: XLSX.Range[] = [];

      // ✅ Country heading first
      aoa.push([country.toUpperCase()]);
      mergeRanges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } });
      aoa.push(["", "", ""]); // spacer row

      // Then institutions and delegates
      sortedOrgs.forEach(([org, list]) => {
        const startRow = aoa.length;

        // Institution row
        aoa.push([org.toUpperCase(), "", ""]);

        // Delegate rows
        const sortedList = list.sort((a, b) =>
          (a.lname || "").localeCompare(b.lname || "")
        );
        sortedList.forEach((delegate) => {
          const delegateName = [delegate.fname, delegate.lname]
            .filter(Boolean)
            .join(" ")
            .trim();
          aoa.push([delegateName, delegate.position || "", "Delegate"]);
        });

        // Blank separator
        aoa.push(["", "", ""]);

        // Mark merge for institution row
        mergeRanges.push({
          s: { r: startRow, c: 0 },
          e: { r: startRow, c: 2 },
        });
      });

      // ✅ Convert to worksheet
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!merges"] = mergeRanges;
      ws["!cols"] = [{ wch: 35 }, { wch: 40 }, { wch: 15 }];
      ws["!pageSetup"] = {
        fitToWidth: 1,
        fitToHeight: 0,
        orientation: "portrait",
      };

      // ✅ Style the country header
      const countryCell = XLSX.utils.encode_cell({ r: 0, c: 0 });
      if (ws[countryCell]) {
        ws[countryCell].s = {
          font: { name: "Arial", sz: 16, bold: true, color: { rgb: "1F4E79" } },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }

      // ✅ Style institution names (bold + centered)
      mergeRanges.forEach((range) => {
        if (range.s.r === 0) return; // skip country heading
        const cellAddr = XLSX.utils.encode_cell({ r: range.s.r, c: 0 });
        if (!ws[cellAddr]) return;
        ws[cellAddr].s = {
          font: { name: "Arial", sz: 14, bold: true, color: { rgb: "000000" } },
          alignment: {
            horizontal: "center",
            vertical: "center",
            wrapText: true,
          },
        };
      });

      XLSX.utils.book_append_sheet(wb, ws, safeName);
    });

    const formattedDate = new Intl.DateTimeFormat("en-GB")
      .format(new Date())
      .replace(/\//g, "_");
    const fileName = `ICA_Delegates_By_Country_${formattedDate}.xlsx`;

    XLSX.writeFile(wb, fileName, { bookType: "xlsx", cellStyles: true });
  };

  const [badgeFilter, setBadgeFilter] = useState<string>("All");

  const handleBadgeFilter = (val: string) => {
    setBadgeFilter(val);

    const allParticipants = registrations.flatMap((r) =>
      r.contactPersons.map((p) => ({
        ...p,
        timestamp: r.timestamp,
        invoiceNumber: r.invoiceNumber,
        registrationKey: r.key,
      }))
    );

    if (val === "All") {
      setFilteredData(allParticipants);
      return;
    }

    const filtered = allParticipants.filter((p) => {
      const badge = p.badgeCategory?.toUpperCase().trim() || "";
      const type = p.ticketType?.toUpperCase().trim() || "";
      const selected = val.toUpperCase();

      // ✅ When filter is "DELEGATE", include UAEFMA Member too
      if (selected === "DELEGATE") {
        return (
          badge === "DELEGATE" ||
          type === "DELEGATE" ||
          type === "UAEFMA MEMBER"
        );
      }

      // ✅ For "EXHIBITOR", match both exhibitor and sponsor if needed
      if (selected === "EXHIBITOR") {
        return badge === "EXHIBITOR" || type === "EXHIBITOR";
      }

      // ✅ Normal case for all others
      return badge === selected || type === selected;
    });

    setFilteredData(filtered);
  };

  // === layout ===
  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {/* LEFT SIDE */}
      <div style={{ flex: 2, padding: 24, overflowY: "auto" }}>
        <h1 className="title" style={{ color: "#2e3c52" }}>
          Badge
        </h1>

        <Tabs defaultActiveKey="delegates">
          <TabPane tab="Participants" key="delegates">
            <div
              style={{
                marginBottom: 16,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Input
                  placeholder="Search delegates..."
                  style={{ width: 400, height: 50 }}
                  allowClear
                  onChange={(e) => handleSearchDelegates(e.target.value)}
                />

                <Select
                  value={badgeFilter}
                  style={{ width: 220, height: 50 }}
                  onChange={(val) => handleBadgeFilter(val)}
                >
                  <Option value="All">All Badge Types</Option>
                  <Option value="DELEGATE">Delegate</Option>
                  <Option value="EXHIBITOR">Exhibitor</Option>
                  <Option value="SPEAKER">Speaker</Option>
                  <Option value="MODERATOR">Moderator</Option>
                  <Option value="VIP">VIP</Option>
                  <Option value="ICA EXECUTIVE">ICA Executive Committee</Option>
                  <Option value="SPONSOR">Sponsor</Option>
                  <Option value="ICA HONORARY">ICA Honorary</Option>
                  <Option value="ORGANIZER">Organizer</Option>
                  <Option value="MEDIA">Media</Option>
                  <Option value="PRESS">Press</Option>
                  <Option value="COMPANION">Companion</Option>
                  <Option value="GUEST">Guest</Option>
                  <Option value="SECURITY">Security</Option>
                  <Option value="BOARD MEMBER">UAE Board Member</Option>
                </Select>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  type="primary"
                  disabled={!selectedRowKeys.length}
                  onClick={() => handleOpenPreview("delegate")}
                >
                  Preview Selected
                </Button>
                <Button onClick={handleClearSelection}>Clear Selection</Button>
                <Button
                  icon={<i className="fa fa-file-excel" />}
                  onClick={handleExportExcelByCountry}
                >
                  Export Excel by Country
                </Button>
              </div>
            </div>

            <Table
              rowSelection={{
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
              }}
              columns={columns}
              dataSource={filteredData}
              rowKey={(r) => `${r.registrationKey}-${r.fname}-${r.lname}`}
              pagination={{
                current: currentPage,
                pageSize,
                onChange: (p) => setCurrentPage(p),
              }}
              bordered
            />
          </TabPane>

          <TabPane tab="Companions" key="companions">
            <Button
              type="primary"
              disabled={!selectedCompanionKeys.length}
              onClick={() => handleOpenPreview("companion")}
              style={{ marginBottom: 12 }}
            >
              Preview Selected
            </Button>
            <Table
              rowSelection={{
                selectedRowKeys: selectedCompanionKeys,
                onChange: (keys) => setSelectedCompanionKeys(keys),
              }}
              columns={[
                { title: "Companion Name", dataIndex: "fname" },
                { title: "Relationship", dataIndex: "companionRelationship" },
                { title: "Country", dataIndex: "country" },
                { title: "Linked Delegate", dataIndex: "name" },
              ]}
              dataSource={filteredCompanions}
              rowKey={(r) => `${r.registrationKey}-${r.fname}`}
              bordered
            />
          </TabPane>

          {/* Custom Tab */}
          <TabPane tab="Custom" key="manual">
            <div className="badge-form">
              <select
                value={manualParticipant.title}
                onChange={(e) =>
                  setManualParticipant({
                    ...manualParticipant,
                    title: e.target.value,
                  })
                }
                className="simple-select"
              >
                <option value="">Title</option>
                <option value="Mr">Mr</option>
                <option value="Ms">Ms</option>
                <option value="Mrs">Mrs</option>
                <option value="Dr">Dr</option>
              </select>
              <Input
                placeholder="First Name"
                value={manualParticipant.fname}
                onChange={(e) =>
                  setManualParticipant({
                    ...manualParticipant,
                    fname: e.target.value,
                  })
                }
              />

              <Input
                placeholder="Last Name"
                value={manualParticipant.lname}
                onChange={(e) =>
                  setManualParticipant({
                    ...manualParticipant,
                    lname: e.target.value,
                  })
                }
              />

              <Input
                placeholder="Organization"
                value={manualParticipant.organization}
                onChange={(e) =>
                  setManualParticipant({
                    ...manualParticipant,
                    organization: e.target.value,
                  })
                }
              />

              <CustomCountrySelect
                value={manualParticipant.country}
                onChange={(val) =>
                  setManualParticipant({ ...manualParticipant, country: val })
                }
                className={manualErrors.country ? "input-error" : ""}
              />

              {/* <Select
              placeholder="Select Type (Delegate, Speaker, Exhibitor, etc.)"
              value={manualParticipant.ticketType}
              onChange={(val) =>
                setManualParticipant({ ...manualParticipant, ticketType: val })
              }
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
            >
              <Option value="DELEGATE">Delegate</Option>
              <Option value="EXHIBITOR">Exhibitor</Option>
              <Option value="SPEAKER">Speaker</Option>
              <Option value="MODERATOR">Moderator</Option>
              <Option value="VIP">VIP</Option>
              <Option value="ICA EXECUTIVE">ICA Executive Committee</Option>
              <Option value="SPONSOR">Sponsor</Option>
              <Option value="ICA HONORARY">ICA Honorary</Option>
              <Option value="ORGANIZER">Organizer</Option>
              <Option value="MEDIA">Media</Option>
              <Option value="PRESS">Press</Option>
              <Option value="COMPANION">Companion</Option>
            </Select> */}

              <select
                value={manualParticipant.badgeCategory}
                onChange={(e) =>
                  setManualParticipant({
                    ...manualParticipant,
                    badgeCategory: e.target.value,
                  })
                }
                className="simple-select"
              >
                <option value="">Select Badge Type</option>
                <option value="DELEGATE">Delegate</option>
                <option value="EXHIBITOR">Exhibitor</option>
                <option value="SPEAKER">Speaker</option>
                <option value="MODERATOR">Moderator</option>
                <option value="VIP">VIP</option>
                <option value="ICA EXECUTIVE">ICA Executive Committee</option>
                <option value="SPONSOR">Sponsor</option>
                <option value="ICA HONORARY">ICA Honorary</option>
                <option value="ORGANIZER">Organizer</option>
                <option value="MEDIA">Media</option>
                <option value="PRESS">Press</option>
                <option value="COMPANION">Companion</option>
                <option value="GUEST">Guest</option>
              </select>

              <Button
                type="primary"
                onClick={() => handleManualBadgePrint()}
                style={{ marginTop: 12 }}
              >
                Preview & Print Badge
              </Button>
            </div>
          </TabPane>

          {/* Print History Tab */}
          <TabPane tab="Badge Print History" key="history">
            <div style={{ marginBottom: 16 }}>
              <Input
                placeholder="Search history by name..."
                style={{ width: 800, height: 53 }}
                allowClear
                onChange={(e) => handleSearchHistory(e.target.value)}
              />
            </div>
            <Table
              columns={historyColumns}
              dataSource={filteredHistory}
              loading={isLoadingHistory}
              rowKey={(_, i: any) => i.toString()}
              pagination={false}
              bordered
            />
          </TabPane>
        </Tabs>
      </div>

      {/* RIGHT SIDE */}
      <div
        style={{
          flex: 1,
          borderLeft: "1px solid #eee",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          background: "#fafafa",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>Print Preview</h3>
          <Button
            type="primary"
            onClick={handlePrintBadges}
            disabled={!pdfPrintUrl || printing}
            loading={printing}
          >
            {printing ? "Printing..." : "Print Badges"}
          </Button>
        </div>

        {loadingPdf ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spin size="large" />
          </div>
        ) : pdfPrintUrl ? (
          <div
            style={{
              position: "relative",
              flex: 1,
              border: "1px solid #ddd",
              borderRadius: 6,
              overflow: "hidden",
              background: "#000",
            }}
          >
            <img
              src="/images/delegate_badge.png"
              alt="Badge Background"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: 0.3,
                zIndex: 0,
              }}
            />
            <iframe
              src={`${pdfPreviewUrl}#toolbar=0`}
              title="Badge Preview"
              style={{
                border: "none",
                width: "100%",
                height: "100%",
                position: "relative",
                zIndex: 1,
              }}

              // src={`${pdfPreviewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              // title="Badge Preview"
              // style={{
              //   border: "none",
              //   width: "100%",
              //   height: "100%",
              // }}
            />
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              border: "2px dashed #ccc",
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#999",
              fontSize: 16,
            }}
          >
            Select records to preview badge
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageRegistrationsV3;
